import classNames from "classnames";
import { ProductLayoutType } from "../../../UserData/_stores/UserPrefsStore";
import { type StoreProductSyncState, StoreProductStore } from "../../../UserData/_stores/Store/StoreProductStore";
import { ButtonsWrapper, ProductCard, ProductCardWrapper, ProductThumbnail, ProductTitle } from "../../Products/ProductDesign";
import { ChangeEvent, MouseEvent, memo, useCallback, useEffect, useMemo, useState } from "react";
import { alpha, Box, Button, Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, styled } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert'
import BlockIcon from '@mui/icons-material/Block'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled'
import SyncProblemRoundedIcon from '@mui/icons-material/SyncProblemRounded'
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined'
import { useTranslation } from "react-i18next";
import DeleteStoreProductDialog from "./DeleteStoreProductDialog";
import { Link } from "react-router";
import DeleteIcon from "../../../Common/Icons/DeleteIcon";
import { useAppSelector } from "../../../Common/_hooks/useAppSelector";
import IgnoreStoreProductDialog from "./IgnoreStoreProductDialog";
import UnsyncStoreProductDialog from "./UnsyncStoreProductDialog";
import EditVariantsDialog from "./EditVariantsDialog";
import { useAppDispatch } from "../../../Common/_hooks/useAppDispatch";
import { SetStoreSelectedProducts } from "../../../UIData/_actions/Account/AccountStoreActions";
import { UnignoreAllStoreVariants } from "../../../UserData/_actions/Store/StoreVariantActions";
import useCheckStoreFeatures from "../../../Common/_hooks/Store/useCheckStoreFeatures";
import usePreventShiftTextSelection, { hasShiftKey, isInteractiveEventTarget } from "../../../Common/_hooks/usePreventShiftTextSelection";
import SelectionCheckbox from "../../SelectionCheckbox";
import SyncIcon from '@mui/icons-material/Sync';

const SyncTag = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  maxWidth: 'calc(100% - ' + theme.spacing(2) + ')',
  pointerEvents: 'none',
}))

const StoreProductThumbnailFallback = styled(Box)(({ theme }) => ({
  width: '100%',
  aspectRatio: '1120 / 1000',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.grey[400],
  flexShrink: 0,

  '& svg': {
    fontSize: '28%',
  },
}))

interface Props {
  item: StoreProductStore
  layout: ProductLayoutType
  selected?: boolean
  onSelect?: (id: number, shiftKey?: boolean) => void
  onUnselect?: (id: number, shiftKey?: boolean) => void
}

const SYNC_STATE_CONFIG: Record<StoreProductSyncState, {
  color: 'gray' | 'info' | 'success' | 'warning'
  icon: JSX.Element
  label: string
}> = {
  ignored: {
    color: 'warning',
    icon: <BlockIcon fontSize="inherit" />,
    label: 'Ignored',
  },
  synced: {
    color: 'success',
    icon: <SyncIcon fontSize="inherit" />,
    label: 'Synced',
  },
  partially: {
    color: 'info',
    icon: <SyncProblemRoundedIcon fontSize="inherit" />,
    label: 'Partially synced',
  },
  'not-synced': {
    color: 'gray',
    icon: <SyncDisabledIcon fontSize="inherit" />,
    label: 'Not synced',
  },
}

function StoreProduct(props: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')
  const storeProductId = props.item.get('id')
  const syncState = props.item.get('sync_state')
  const isIgnored = syncState === 'ignored'

  const store = useAppSelector(state => state.get('userData').get('stores').get(props.item.get('id_store')))
  const storeName = store?.get('name') ?? ''
  const storeType = store?.get('slug') ?? ''
  const { canDeleteRemoteProduct } = useCheckStoreFeatures(storeType)
  const isSelectedFromStore = useAppSelector(state => {
    const account = state.get('UIData').get('account')

    if (account.get('storeSelectedAllProducts')) {
      return !account.get('storeExcludedSelectedProducts').has(storeProductId)
    }

    return account.get('storeSelectedProducts').has(storeProductId)
  })
  const isSelected = props.selected ?? isSelectedFromStore

  const [menuOpened, setMenuOpened] = useState(false)
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [editVariantsDialogOpened, setEditVariantsDialogOpened] = useState(false)
  const [unsyncDialogOpened, setUnsyncDialogOpened] = useState(false)
  const [ignoreDialogOpened, setIgnoreDialogOpened] = useState(false)
  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false)
  const [previewError, setPreviewError] = useState(false)

  const onMenuOpen = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget)
    setMenuOpened(true)
  }, [])

  const onMenuClose = useCallback(() => {
    setMenuOpened(false)
    setAnchorEl(null)
  }, [])

  const onEditVariantsClose = useCallback(() => {
    setEditVariantsDialogOpened(false)
  }, [])

  const onEditVariantsOpen = useCallback(() => {
    setMenuOpened(false)
    setEditVariantsDialogOpened(true)
  }, [])

  const onUnsyncClose = useCallback(() => {
    setUnsyncDialogOpened(false)
  }, [])

  const onUnsyncOpen = useCallback(() => {
    setMenuOpened(false)
    setUnsyncDialogOpened(true)
  }, [])

  const onIgnoreClose = useCallback(() => {
    setIgnoreDialogOpened(false)
  }, [])

  const onIgnoreOpen = useCallback(() => {
    setMenuOpened(false)
    setAnchorEl(null)
    setIgnoreDialogOpened(true)
  }, [])

  const onUnignore = useCallback(() => {
    setMenuOpened(false)
    setAnchorEl(null)

    if (!storeType) {
      return
    }

    dispatch(UnignoreAllStoreVariants(storeType, storeProductId))
  }, [dispatch, storeProductId, storeType])

  const onDeleteClose = useCallback(() => {
    setDeleteDialogOpened(false)
  }, [])

  const onDeleteOpen = useCallback(() => {
    setMenuOpened(false)
    setDeleteDialogOpened(true)
  }, [])

  const onChangeCheckbox = useCallback((e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const shiftKey = hasShiftKey(e.nativeEvent)

    if (checked) {
      if (props.onSelect) {
        props.onSelect(storeProductId, shiftKey)
      } else {
        dispatch(SetStoreSelectedProducts([storeProductId], true))
      }
    } else {
      if (props.onUnselect) {
        props.onUnselect(storeProductId, shiftKey)
      } else {
        dispatch(SetStoreSelectedProducts([storeProductId], false))
      }
    }
  }, [dispatch, props.onSelect, props.onUnselect, storeProductId])

  const onClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (isInteractiveEventTarget(e.target)) {
      return
    }

    if (!isSelected) {
      if (props.onSelect) {
        props.onSelect(storeProductId, e.shiftKey)
      } else {
        dispatch(SetStoreSelectedProducts([storeProductId], true))
      }
    } else {
      if (props.onUnselect) {
        props.onUnselect(storeProductId, e.shiftKey)
      } else {
        dispatch(SetStoreSelectedProducts([storeProductId], false))
      }
    }
  }, [dispatch, isSelected, props.onSelect, props.onUnselect, storeProductId])

  const onMouseDown = usePreventShiftTextSelection<HTMLDivElement>()

  const syncStateConfig = useMemo(() => SYNC_STATE_CONFIG[syncState], [syncState])
  const syncStateLabel = t(syncStateConfig.label)
  const preview = props.item.get('preview') ?? ''
  const hasPreview = preview.trim() !== '' && !previewError

  useEffect(() => {
    setPreviewError(false)
  }, [preview])

  const onPreviewError = useCallback(() => {
    setPreviewError(true)
  }, [])

  return <ProductCardWrapper item className={props.layout === 'list' ? 'list-view' : ''}>
    <ProductCard
      elevation={props.layout === 'grid' ? 4 : 0}
      className={classNames({
        selected: isSelected,
        clickable: true,
      })}
      onClick={onClick}
      onMouseDown={onMouseDown}
    >
      <SelectionCheckbox
        checked={isSelected}
        onChange={onChangeCheckbox}
        sx={props.layout === 'grid' ? {
          position: 'absolute',
          top: '5px',
          left: '5px',
          zIndex: 1,
        } : {
          mr: 2,
        }}
      />
      {hasPreview ? <ProductThumbnail
        src={preview}
        alt={props.item.get('title')}
        draggable={false}
        onError={onPreviewError}
      /> : <StoreProductThumbnailFallback
        aria-hidden="true"
        sx={props.layout === 'list' ? {
          width: '80px',
          borderRadius: '15px',
        } : undefined}
      >
        <ImageNotSupportedOutlinedIcon />
      </StoreProductThumbnailFallback>}
      <ProductTitle>
        <Stack spacing={1} sx={{alignItems: 'flex-start'}}>
          {props.item.get('title')}
          <SyncTag
            color={syncStateConfig.color}
            icon={syncStateConfig.icon}
            label={syncStateLabel}
            sx={{
              display: props.layout === 'list' ? 'inline-flex' : 'none',
              position: 'static',
            }}
            variant="badge"
          />
        </Stack>
      </ProductTitle>
      <ButtonsWrapper>
        <Button
          onClick={onEditVariantsOpen}
          variant="outlined"
          color="secondary"
          sx={{
            display: props.layout === 'grid' ? 'inline-flex' : {
              xs: 'none',
              lg: 'inline-flex',
            }
          }}
        >
          <Box component="span" sx={{
            display: {
              xs: 'none',
              sm: 'inline',
            }
          }}>{t('Edit variants')}</Box>
          <Box component="span" sx={{
            display: {
              xs: 'inline',
              sm: 'none',
            }
          }}>{t('Variants')}</Box>
        </Button>
        <IconButton
          onClick={onMenuOpen}
          size="large">
          <MoreVertIcon />
        </IconButton>
      </ButtonsWrapper>
      <SyncTag
        color={syncStateConfig.color}
        icon={syncStateConfig.icon}
        label={syncStateLabel}
        sx={{
          display: props.layout === 'grid' ? 'inline-flex' : 'none',
          maxWidth: theme => 'calc(100% - ' + theme.spacing(8) + ')',
          color: theme => syncStateConfig.color === 'gray' ? theme.palette.text.secondary : theme.palette[syncStateConfig.color].main,
          backgroundColor: theme => alpha(theme.palette.common.white, 0.92),
          borderColor: theme => syncStateConfig.color === 'gray'
            ? alpha(theme.palette.text.secondary, 0.22)
            : alpha(theme.palette[syncStateConfig.color].main, 0.28),
        }}
        variant="badge"
      />
    </ProductCard>
    {menuOpened ? <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      open={menuOpened}
      onClose={onMenuClose}
    >
      <MenuItem
        onClick={onEditVariantsOpen}
        sx={{
          display: props.layout === 'grid' ? 'none' : {
            xs: 'block',
            lg: 'none',
          },
          color: 'secondary.main',
          '& .MuiListItemIcon-root': {
            color: 'secondary.main',
          },
        }}
      >
        <ListItemText>{t('Edit variants')}</ListItemText>
      </MenuItem>
      <MenuItem
        component={Link}
        to={props.item.get('store_url')}
        target="_blank"
      >
        <ListItemIcon><OpenInNewIcon /></ListItemIcon>
        <ListItemText>{t('Edit on {{store}}', { store: storeName })}</ListItemText>
      </MenuItem>
      <MenuItem 
        onClick={isIgnored ? onUnignore : onIgnoreOpen} 
        sx={{
          color: 'warning.main',
          '& .MuiListItemIcon-root': {
            color: 'warning.main',
          },
        }}
      >
        <ListItemIcon><BlockIcon /></ListItemIcon>
        <ListItemText>{t(isIgnored ? 'Unignore' : 'Ignore')}</ListItemText>
      </MenuItem>
      <MenuItem 
        onClick={onUnsyncOpen} 
        sx={{
          color: 'secondary.main',
          '& .MuiListItemIcon-root': {
            color: 'secondary.main',
          },
        }}
      >
        <ListItemIcon><SyncDisabledIcon /></ListItemIcon>
        <ListItemText>{t('Unsync')}</ListItemText>
      </MenuItem>
      {canDeleteRemoteProduct ? <MenuItem 
        onClick={onDeleteOpen} 
        sx={{
          color: 'error.main',
          '& .MuiListItemIcon-root': {
            color: 'error.main',
          },
        }}
      >
        <ListItemIcon><DeleteIcon /></ListItemIcon>
        <ListItemText>{t('Delete')}</ListItemText>
      </MenuItem> : null}
    </Menu> : null}

    {editVariantsDialogOpened ? <EditVariantsDialog
      storeProduct={props.item}
      opened={editVariantsDialogOpened}
      onClose={onEditVariantsClose}
    /> : null}

    {ignoreDialogOpened ? <IgnoreStoreProductDialog
      storeProduct={props.item}
      opened={ignoreDialogOpened}
      onClose={onIgnoreClose}
    /> : null}

    {unsyncDialogOpened ? <UnsyncStoreProductDialog
      storeProduct={props.item}
      opened={unsyncDialogOpened}
      onClose={onUnsyncClose}
    /> : null}

    {deleteDialogOpened && canDeleteRemoteProduct ? <DeleteStoreProductDialog
      storeProduct={props.item}
      opened={deleteDialogOpened}
      onClose={onDeleteClose}
    /> : null}
  </ProductCardWrapper >
}

export default memo(StoreProduct)
